import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';

const VenueResponsePage = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const action = searchParams.get('action');

  const [loading, setLoading] = useState(true);
  const [reservation, setReservation] = useState(null);
  const [proposedTime, setProposedTime] = useState('');
  const [proposedDuration, _setProposedDuration] = useState('');
  const [notes, setNotes] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadReservationPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Convert reservation date/time to datetime-local format in venue timezone
  useEffect(() => {
    if (reservation?.reserved_at && reservation?.venues?.timezone) {
      const date = new Date(reservation.reserved_at);
      const timezone = reservation.venues.timezone;

      // Format to datetime-local format (YYYY-MM-DDTHH:mm)
      const localDateTimeString = date.toLocaleString('sv-SE', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }).replace(' ', 'T');

      setProposedTime(localDateTimeString);
    }
  }, [reservation]);

  const loadReservationPreview = async () => {
    try {
      // Get token details first
      const { data: tokenData, error: tokenError } = await supabase
        .from('venue_response_tokens')
        .select('reservation_id')
        .eq('token', token)
        .is('used_at', null)
        .gt('expires_at', new Date().toISOString())
        .single();

      if (tokenError) throw tokenError;
      if (!tokenData) throw new Error('Token not found or expired');

      // Get reservation with related data
      const { data: reservationData, error: reservationError } = await supabase
        .from('reservations')
        .select(`
          *,
          venues(*),
          teams(*)
        `)
        .eq('id', tokenData.reservation_id)
        .single();

      if (reservationError) throw reservationError;
      if (!reservationData) throw new Error('Reservation not found');

      setReservation(reservationData);
    } catch (err) {
      // Error caught and ignored
      console.error('Error loading reservation:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Convert proposed time from venue timezone to UTC if provided
      let proposedTimeUTC = proposedTime;
      if (proposedTime && reservation?.venues?.timezone) {
        // The datetime-local input gives us a string like "2025-11-09T16:00"
        // We need to interpret this as being in the venue's timezone and convert to UTC
        const venueTimezone = reservation.venues.timezone;

        // Parse the datetime-local value (YYYY-MM-DDTHH:mm)
        const parts = proposedTime.match(/(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
        if (parts) {
          const [, year, month, day, hour, minute] = parts;

          // Create a date string that represents the time in the venue's timezone
          // Format: "YYYY-MM-DD HH:MM:SS"
          const _venueTimeString = `${year}-${month}-${day} ${hour}:${minute}:00`;

          // Use toLocaleString to format an arbitrary UTC date in the venue timezone
          // This helps us calculate the offset for the specific date (accounts for DST)
          const testDate = new Date(Date.UTC(parseInt(year), parseInt(month) - 1, parseInt(day), 12, 0, 0));
          const testFormatted = testDate.toLocaleString('sv-SE', { // ISO format
            timeZone: venueTimezone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
          });

          // Parse back to Date to calculate offset
          const testParsed = new Date(testFormatted.replace(' ', 'T'));
          const offsetMs = testDate - testParsed;

          // Now create our target time treating the input as local browser time
          // Then apply the offset to convert from venue time to UTC
          const targetDate = new Date(`${year}-${month}-${day}T${hour}:${minute}:00`);
          const utcDate = new Date(targetDate.getTime() + offsetMs);

          proposedTimeUTC = utcDate.toISOString();

          // Verify the conversion by converting back
          const verification = utcDate.toLocaleString('en-US', {
            timeZone: venueTimezone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          });

          console.log('Timezone conversion:', {
            input: proposedTime,
            venueTimezone,
            offsetMs,
            offsetHours: offsetMs / (1000 * 60 * 60),
            outputUTC: proposedTimeUTC,
            verification: verification
          });
        }
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/process-reservation-response`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            token,
            action,
            responder_type: 'venue',
            proposed_time: proposedTimeUTC || undefined,
            proposed_duration_hours: proposedDuration ? parseFloat(proposedDuration) : undefined,
            notes: notes || undefined,
          }),
        }
      );

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to process response');
      }

      setSubmitted(true);
    } catch (err) {
      // Error caught and ignored
      console.error('Error processing response:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !reservation) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-navy-600 to-burgundy-700 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy-600 mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (error && !reservation) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-navy-600 to-burgundy-700 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Error</h1>
            <p className="text-gray-600 mb-4">{error}</p>
            <p className="text-sm text-gray-500">
              This link may have expired or already been used. If you need assistance, please contact the team directly.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-navy-600 to-burgundy-700 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Response Submitted</h1>
            <p className="text-gray-600 mb-4">Thank you! The team has been notified of your response.</p>

            {action === 'accept' && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <p className="text-green-800">
                  You have confirmed the reservation. The team will receive a confirmation email.
                </p>
              </div>
            )}

            {action === 'propose_time' && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <p className="text-yellow-800">
                  You have proposed a different time. The team will review and respond to your proposal.
                </p>
              </div>
            )}

            {action === 'reject' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <p className="text-red-800">
                  You have declined the reservation. The team has been notified.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return '';
    const date = new Date(dateTimeString);
    const timezone = reservation?.venues?.timezone || 'UTC';
    return date.toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: timezone,
      timeZoneName: 'short'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy-600 to-burgundy-700 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-2xl">
        <div className="mb-6">
          <div className="flex items-center justify-center mb-4">
            <div className="text-4xl font-bold text-navy-600">TeamRezy</div>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 text-center mb-2">
            {action === 'accept' && 'Accept Reservation'}
            {action === 'propose_time' && 'Propose Different Time'}
            {action === 'reject' && 'Decline Reservation'}
          </h1>
          <p className="text-gray-600 text-center">
            Please review the reservation details below
          </p>
        </div>

        {/* Reservation Summary */}
        <div className="bg-gray-50 rounded-lg p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Reservation Details</h2>
          <div className="space-y-3">
            {reservation?.teams?.name && (
              <div className="flex justify-between">
                <span className="text-gray-600">Team:</span>
                <span className="font-semibold text-gray-900">{reservation.teams.name}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-gray-600">Date & Time:</span>
              <span className="font-semibold text-gray-900">{formatDateTime(reservation?.reserved_at)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Group Size:</span>
              <span className="font-semibold text-gray-900">{reservation?.group_size} people</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Confirmation #:</span>
              <span className="font-semibold text-gray-900">{reservation?.confirmation_number}</span>
            </div>
            {reservation?.total_cost && (
              <div className="flex justify-between">
                <span className="text-gray-600">Deposit Received:</span>
                <span className="font-semibold text-gray-900">${reservation.total_cost.toFixed(2)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {action === 'propose_time' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Proposed Date & Time *
              </label>
              <input
                type="datetime-local"
                value={proposedTime}
                onChange={(e) => setProposedTime(e.target.value)}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy-500 focus:border-transparent"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes (optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              placeholder="Add any additional information or comments..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy-500 focus:border-transparent resize-none"
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className={`w-full py-3 px-6 rounded-lg font-semibold text-white transition-colors ${
              loading
                ? 'bg-gray-400 cursor-not-allowed'
                : action === 'accept'
                ? 'bg-green-600 hover:bg-green-700'
                : action === 'propose_time'
                ? 'bg-yellow-600 hover:bg-yellow-700'
                : 'bg-red-600 hover:bg-red-700'
            }`}
          >
            {loading ? 'Submitting...' : 'Submit Response'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default VenueResponsePage;
